import React, { useState, useEffect } from 'react'
import {
	Card,
	Form,
	Input,
	InputNumber,
	Button,
	message,
	Spin,
	Typography,
	Table,
	Space,
	Modal,
	Popconfirm,
	Tooltip,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { httpGet, httpPost, httpPatch, httpDelete } from '../../common/http.service'

const { Text } = Typography

interface ShiftRange {
	start: string
	end: string
}

interface DynamicShift {
	id: string
	name: string
	team: string
	shift?: ShiftRange
	createdAt?: string
}

const DynamicShiftTab: React.FC = () => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [shifts, setShifts] = useState<DynamicShift[]>([])
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [editingShift, setEditingShift] = useState<DynamicShift | null>(null)

	useEffect(() => {
		loadShifts()
	}, [])

	const loadShifts = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/shift/dynamic')
			if (response?.data) {
				setShifts(response.data)
			}
		} catch (error) {
			console.error('Failed to load dynamic shifts:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleCreate = () => {
		setEditingShift(null)
		form.resetFields()
		setIsModalVisible(true)
	}

	const handleEdit = (shift: DynamicShift) => {
		setEditingShift(shift)
		form.setFieldsValue({
			id: shift.id,
			name: shift.name,
			team: shift.team,
			shiftStart: shift.shift?.start,
			shiftEnd: shift.shift?.end,
		})
		setIsModalVisible(true)
	}

	const handleDelete = async (id: string) => {
		try {
			await httpDelete(`/api/v1/admin/shift/dynamic/${id}`)
			message.success('Dynamic shift deleted successfully')
			loadShifts()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to delete dynamic shift')
		}
	}

	const handleSubmit = async (values: any) => {
		try {
			const payload: any = {
				id: values.id,
				name: values.name,
				team: values.team,
			}

			if (values.shiftStart && values.shiftEnd) {
				payload.shift = {
					start: values.shiftStart,
					end: values.shiftEnd,
				}
			}

			if (editingShift) {
				await httpPatch('/api/v1/admin/shift/dynamic', payload)
				message.success('Dynamic shift updated successfully')
			} else {
				await httpPost('/api/v1/admin/shift/dynamic', payload)
				message.success('Dynamic shift created successfully')
			}

			setIsModalVisible(false)
			form.resetFields()
			loadShifts()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to save dynamic shift')
		}
	}

	const columns = [
		{
			title: 'Shift ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Shift Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Team',
			dataIndex: 'team',
			key: 'team',
		},
		{
			title: 'Shift Range',
			key: 'shift',
			render: (record: DynamicShift) =>
				record.shift ? `${record.shift.start} - ${record.shift.end}` : '-',
		},
		{
			title: 'Action',
			key: 'action',
			render: (record: DynamicShift) => (
				<Space size='small'>
					<Tooltip title='Edit shift'>
						<Button
							type='link'
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						>
							Edit
						</Button>
					</Tooltip>
					<Popconfirm
						title='Are you sure you want to delete this shift?'
						onConfirm={() => handleDelete(record.id)}
						okText='Yes'
						cancelText='No'
					>
						<Button type='link' danger icon={<DeleteOutlined />}>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<>
			<Card
				title='Dynamic Shift Management'
				bordered={false}
				style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
				extra={
					<Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
						Create Shift
					</Button>
				}
			>
				<Spin spinning={loading}>
					<Table
						columns={columns}
						dataSource={shifts}
						rowKey='id'
						pagination={{ pageSize: 10 }}
						locale={{
							emptyText: (
								<div style={{ padding: '40px 0' }}>
									<Text type='secondary'>
										No dynamic shifts found. Click "Create Shift" to add one.
									</Text>
								</div>
							),
						}}
					/>
				</Spin>
			</Card>

			<Modal
				title={editingShift ? 'Edit Dynamic Shift' : 'Create Dynamic Shift'}
				open={isModalVisible}
				onOk={() => form.submit()}
				onCancel={() => {
					setIsModalVisible(false)
					form.resetFields()
				}}
				width={600}
				okText={editingShift ? 'Update' : 'Create'}
			>
				<Form form={form} layout='vertical' onFinish={handleSubmit}>
					<Form.Item
						name='id'
						label='Shift ID'
						rules={[{ required: true, message: 'Please enter shift ID' }]}
					>
						<Input placeholder='e.g., PM-1, NIGHT-1' disabled={!!editingShift} />
					</Form.Item>

					<Form.Item
						name='name'
						label='Shift Name'
						rules={[{ required: true, message: 'Please enter shift name' }]}
					>
						<Input placeholder='e.g., PM Shift, Night Shift' />
					</Form.Item>

					<Form.Item
						name='team'
						label='Team'
						rules={[{ required: true, message: 'Please enter team name' }]}
					>
						<Input placeholder='e.g., PM-2, NIGHT-1' />
					</Form.Item>

					<Form.Item label='Shift Time Range (Optional)' style={{ marginBottom: 0 }}>
						<Form.Item
							name='shiftStart'
							style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
						>
							<Input placeholder='Start time (e.g., 14:00)' />
						</Form.Item>
						<span
							style={{
								display: 'inline-block',
								width: '16px',
								textAlign: 'center',
							}}
						>
							-
						</span>
						<Form.Item
							name='shiftEnd'
							style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
						>
							<Input placeholder='End time (e.g., 22:00)' />
						</Form.Item>
					</Form.Item>
				</Form>
			</Modal>
		</>
	)
}

export default DynamicShiftTab
